import { Grid, MenuItem, styled, TextField } from '@mui/material'
import { ClientType, SelectClientContract } from 'models'
import {
    IRegisterDeviceForm,
    RegisterDeviceTab,
} from 'pages/RegisterDevicePage/RegisterDevicePage'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import nameof from 'utilities/nameof'
import WhiteTextField from 'views/CreateCompany/components/WhiteTextField'

const fields = nameof<IRegisterDeviceForm>()

const TextFieldRoundCss = {
    borderRadius: 4,
}

const InputBG = {
    color: '#000',
    background: '#fff',
    height: '50px',
}

const TextFieldRound = styled(TextField)({
    ...TextFieldRoundCss,
    [`& .MuiInputBase-root`]: InputBG,
    whiteSpace: 'normal',
})

interface RegisterDeviceFormProps {
    tab: RegisterDeviceTab
    clients: SelectClientContract[]
}

function RegisterDeviceForm({ tab, clients }: RegisterDeviceFormProps) {
    const { control, formState, setValue } = useFormContext<
        IRegisterDeviceForm
    >()
    const formValues = useWatch({ control })

    const selectFirstDevice = useCallback(
        (clientType: string) => {
            const clientInCurrentType = clients.filter(
                (c) => c.type === clientType,
            )
            setValue('token', clientInCurrentType[0].tokenKey!, {
                shouldValidate: true,
            })
        },
        [clients, setValue],
    )

    const onChange = useCallback(
        (event: any) => {
            setValue('clientType', event.target.value, { shouldValidate: true })
            if (tab === RegisterDeviceTab.EXISTING)
                selectFirstDevice(event.target.value)
        },
        [selectFirstDevice, setValue, tab],
    )

    const shouldDisabledPosTerminalWithCash = useMemo(
        () =>
            tab === RegisterDeviceTab.EXISTING &&
            clients.every((c) => c.type !== ClientType.PosTerminalWithCash),
        [clients, tab],
    )
    const shouldDisabledPosTerminalWithoutCash = useMemo(
        () =>
            tab === RegisterDeviceTab.EXISTING &&
            clients.every((c) => c.type !== ClientType.PosTerminalWithOutCash),
        [clients, tab],
    )

    const clientLists = useMemo(
        () => clients.filter((f) => f.type === formValues.clientType),
        [clients, formValues.clientType],
    )

    // default first existing client
    useEffect(() => {
        if (clientLists.length === 0 || !formValues.clientType) return
        if (tab === RegisterDeviceTab.EXISTING)
            selectFirstDevice(formValues.clientType)
    }, [
        clientLists.length,
        clients,
        setValue,
        tab,
        formValues.clientType,
        selectFirstDevice,
    ])

    return (
        <Grid container direction="column" justifyContent="center" p={0} m={0}>
            <Grid item>
                <TextFieldRound
                    select
                    onChange={(e) => onChange(e)}
                    value={formValues.clientType}
                    name={fields.clientType}
                    fullWidth
                    sx={{
                        maxWidth: { xs: 250, sm: '100%' },
                        minWidth: { md: 350, xs: undefined },
                    }}
                >
                    <MenuItem
                        value={ClientType.PosTerminalWithCash}
                        disabled={shouldDisabledPosTerminalWithCash}
                    >
                        POS Terminal (with Cash Drawer Control)
                    </MenuItem>
                    <MenuItem
                        value={ClientType.PosTerminalWithOutCash}
                        disabled={shouldDisabledPosTerminalWithoutCash}
                    >
                        POS Terminal
                    </MenuItem>
                </TextFieldRound>
            </Grid>
            <Grid item mt={2}>
                {tab === RegisterDeviceTab.REGISTER ? (
                    <WhiteTextField
                        label={'Device Name'}
                        onChange={(e) =>
                            setValue('name', e.target.value, {
                                shouldValidate: true,
                            })
                        }
                        value={formValues.name}
                        name={fields.name}
                        autoComplete={'off'}
                        sx={{
                            ...TextFieldRoundCss,
                            maxWidth: { xs: 250, sm: '100%' },
                            minWidth: { md: 350, xs: undefined },
                        }}
                        InputProps={{
                            sx: InputBG,
                        }}
                        fullWidth
                        error={Boolean(formState.errors.name)}
                        helperText={formState.errors.name?.message}
                    />
                ) : (
                    <TextFieldRound
                        select
                        onChange={(e) =>
                            setValue('token', e.target.value, {
                                shouldValidate: true,
                            })
                        }
                        value={formValues.token}
                        name={fields.token}
                        fullWidth
                        error={Boolean(formState.errors.token)}
                        helperText={formState.errors.token?.message}
                        sx={{
                            maxWidth: { xs: 250, sm: '100%' },
                            minWidth: { md: 350, xs: undefined },
                        }}
                    >
                        {clientLists.map((m) => (
                            <MenuItem key={m.tokenKey} value={m.tokenKey!!}>
                                {m.name}
                            </MenuItem>
                        ))}
                    </TextFieldRound>
                )}
            </Grid>
        </Grid>
    )
}
export default RegisterDeviceForm
